import { useAtom, useAtomValue } from 'jotai';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Edit } from '@carbon/react/icons';
import {
  Button,
  SkeletonText,
  Tag,
  Tooltip,
  FlexGrid as Grid,
  Row,
  Column,
  InlineNotification,
  Tile,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  DataTableSkeleton,
  Loading,
} from '@carbon/react';
import { DevelopmentAreasVersionSwitcher } from 'components/development-areas/DevelopmentAreasVersionSwitcher';
import { Link, useLocation, useParams } from 'react-router-dom';
import { RoyaltyRateModal } from 'components/allocation-manager/ownership-tables/modals/royalty-rate-modal';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';

import { DevelopmentAreaWizard } from 'components/development-area-wizard';
import { PageHeader } from 'components/page-header';
import { Section } from 'components/section';
import { RelatedItemsSection } from 'components/related-items';
import { SupportingFiles } from 'components/well-page-content/supporting-files';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { AllocationTractMap } from 'components/allocation-manager/allocation-tract-map/allocation-tract-map';
import { AllocationManager } from 'components/allocation-manager/allocation-tracts-table';
import { SuccessToast } from 'components/success-toast';
import { AllocationManagerHeaderActions } from 'components/allocation-manager/allocation-tracts-table/components/allocation-manager-header-actions';
import { SlotsDetail } from 'components/allocation-manager/slot-details';
import { ComparisonModal } from 'components/allocation-manager/comparison-modal';
import { UnitDetailsRibbon } from 'components/allocation-manager/unit-details-ribbon';
import { TitleAssetsTable } from 'components/allocation-manager/ownership-tables';
import { AllocationMethodUpdateModal } from 'components/allocation-manager/allocation-method-update-modal/allocation-method-update-modal';
import { clearNriTableVerificationState } from 'components/nri-table/atoms';

// Utils & Types
import {
  DevelopmentAreaResponse,
  AllocationManagerResponse,
  AllocationMethodUpdateResponse,
  AllocationMethodUnchangedResponse,
} from 'types/api-responses';
import { subscribe } from 'lib/pusher';
import { pageTitle } from 'utils/page-title';
import { Page } from './types';

// Styles
import style from './page.module.scss';

// Constants
import {
  ASSET_TYPES,
  AssetType,
  DEFAULT_PARTICIPATION_FACTOR_THRESHOLD,
  ROYALTY_RATE_OPTIONS,
} from 'components/allocation-manager/ownership-tables/constants';

// Atoms
import {
  useDevelopmentAreaAtom,
  useWellAtom,
  useAllocationManagerAtom,
  useUnitByNameAtom,
  useAllocationManagerVersionsAtom,
  useDevelopmentAreaVersionAtom,
  useWellFeaturesAtom,
  allocationMethodUpdateAtom,
} from './atoms';
import { NriTable } from './nri-table';
import { DevelopmentAreaAliases } from './development-area-aliases';
import { isEmpty } from 'lodash-es';
import { TractNRI } from 'components/nri-table';
import { CreateDcaWorkspace } from 'components/dca-workspace/create-dca-workspace';

/**
 * Skeleton for the allocation manager table while it loads.
 *
 * @return {JSX.Element} The skeleton.
 */
function LoadingAllocationManager() {
  return (
    <DataTableSkeleton
      rowCount={5}
      columnCount={5}
      headers={[
        { header: 'Tract' },
        { header: 'Acreage' },
        { header: 'Factor' },
        { header: 'GIS Acreage' },
        { header: 'Status' },
      ]}
    />
  );
}

/**
 * Spinner for the allocation tract map while it loads.
 *
 * @return {JSX.Element} The spinner.
 */
function LoadingAllocationTractMap() {
  return (
    <div className={style.mapLoadingContainer}>
      <Loading active description="Loading Map..." withOverlay={false} small />
    </div>
  );
}

// The selected well's Related Items, mirroring the section on the well page.
// Well-scoped, not area-scoped: this replaced a list of that well's files.
const RelatedWellItemsSection = ({
  selectedWell,
}: {
  selectedWell: ReturnType<typeof useWellAtom>;
}) => {
  const wellData = useAtomValue(selectedWell);
  if (!wellData) return null;
  if (!wellData.data || 'error' in wellData.data) return null;
  return (
    <RelatedItemsSection
      host={{
        basePath: `/wells/${wellData.data.uwi}`,
        notifications: wellData.data.notifications,
      }}
    />
  );
};

// The selected well's read-only Related Well Files, shown with a deprecation
// notice while existing files migrate to Related Items.
const RelatedWellFilesSection = ({
  selectedWell,
}: {
  selectedWell: ReturnType<typeof useWellAtom>;
}) => {
  const wellData = useAtomValue(selectedWell);
  if (!wellData) return null;
  if (!wellData.data || 'error' in wellData.data) return null;
  return (
    <SupportingFiles href={wellData.data.resources.supportingFiles.href} />
  );
};

/** The well the page is focused on — chosen in Slots Details, read by the header. */
interface SelectedWell {
  allocationManagerId: number | null;
  uwi: string | null;
  wellName: string | null;
  slotUnit: string | null;
}

/**
 * The well a unit opens to before the user picks a row.
 *
 * Prefers the first assignment that has an allocation manager so units returned by
 * the "has allocation managers" filter don't open to a well without one (INS-8488).
 * Falls back to the first assignment when none have an AM.
 *
 * @param {DevelopmentAreaResponse | undefined} developmentArea - The unit being viewed.
 * @return {SelectedWell} The default selection; all-null when the unit has no wells.
 */
const defaultSelectedWell = (
  developmentArea: DevelopmentAreaResponse | undefined
): SelectedWell => {
  const assignments = developmentArea?.unitWellAssignments ?? [];
  const defaultAssignment =
    assignments.find((uwa) => uwa.allocationManagerId !== null) ??
    assignments[0];

  return {
    allocationManagerId: defaultAssignment?.allocationManagerId ?? null,
    uwi: defaultAssignment?.wellUWI ?? null,
    wellName: null,
    // Every unitWellAssignment belongs to this unit, so the default row is
    // this unit's row. Naming it keeps the initial highlight on that one row
    // now that a well can appear under more than one agreement.
    slotUnit: defaultAssignment ? (developmentArea?.name ?? null) : null,
  };
};

// Header Actions component
const Actions = ({
  developmentArea,
  getDevArea,
}: {
  developmentArea: DevelopmentAreaResponse;
  getDevArea: () => Promise<void>;
}) => {
  const [editIsOpen, toggleEditIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  if (!developmentArea) return null;

  return developmentArea.isImported ? (
    <>
      <CreateDcaWorkspace
        parentType="DevelopmentArea"
        parentId={developmentArea.id}
      />
      <Tooltip
        open={showTooltip}
        align="top"
        label="Unable to edit imported Development Areas"
      >
        <Button
          renderIcon={Edit}
          disabled
          size="sm"
          onPointerEnter={() => setShowTooltip(true)}
          onPointerLeave={() => setShowTooltip(false)}
        >
          <span>Edit Development Area</span>
        </Button>
      </Tooltip>
      <EventsPanelOpenButton />
    </>
  ) : (
    <>
      <DevelopmentAreaWizard
        developmentArea={developmentArea}
        isOpen={editIsOpen}
        onClose={() => toggleEditIsOpen(false)}
        onCreated={() => {
          toggleEditIsOpen(false);
          getDevArea();
        }}
      />
      <Button
        renderIcon={Edit}
        onClick={() => toggleEditIsOpen(true)}
        size="sm"
      >
        Edit Development Area
      </Button>
      <EventsPanelOpenButton />
    </>
  );
};

// Header component
const Header = ({
  developmentArea,
  getDevArea,
  page,
  setPage,
}: {
  developmentArea: DevelopmentAreaResponse | undefined;
  getDevArea: () => Promise<void>;
  page: Page;
  setPage: (page: Page) => void;
}) => {
  const { pathname } = useLocation();

  const devAreaVersionAtom = useDevelopmentAreaVersionAtom(
    developmentArea?.resources.versions.href
  );
  const devAreaVersions = useAtomValue(devAreaVersionAtom);

  if (devAreaVersions.loading || devAreaVersions.data === undefined) {
    return <SkeletonText className={style.versionSwitcherSkeleton} />;
  }

  if ('error' in devAreaVersions.data) {
    return (
      <InlineNotification
        kind="error"
        title={'Error loading calculation details for this interpretation'}
        lowContrast
      />
    );
  }

  return (
    <PageHeader
      breadcrumbs={[
        ['/development_areas', 'Development areas'],
        [pathname, developmentArea?.name || <SkeletonText width="200px" />],
      ]}
      title={developmentArea?.name || <SkeletonText heading width="200px" />}
      actions={
        developmentArea ? (
          <Actions developmentArea={developmentArea} getDevArea={getDevArea} />
        ) : null
      }
      attributes={
        developmentArea ? (
          <DevelopmentAreaAliases developmentArea={developmentArea} />
        ) : null
      }
      titleDetail={
        developmentArea ? (
          <DevelopmentAreasVersionSwitcher
            developmentAreaVersions={devAreaVersions.data}
            setPage={setPage}
            page={page}
          />
        ) : (
          <SkeletonText
            className={style.versionSwitcherSkeleton}
            width={'30px'}
          />
        )
      }
    />
  );
};

// Events Content component
const EventsContent = ({
  developmentArea,
}: {
  developmentArea: DevelopmentAreaResponse;
}) => (
  <EventsPanel
    header={developmentArea.name}
    title="Development Area"
    helperText="Development Area context allows you to share information about a development area."
    eventsHref={developmentArea.resources.events.href}
    commentsHref={developmentArea.resources.comments.href}
  />
);

// Main Page Content component
const PageContent = ({
  developmentArea,
  page,
  isAllocationMethodModalOpen,
  closeAllocationMethodModal,
  openAllocationMethodModal,
  getDevArea,
  onAllocationManagerChange,
}: {
  developmentArea: DevelopmentAreaResponse;
  page: Page;
  isAllocationMethodModalOpen: boolean;
  closeAllocationMethodModal: () => void;
  openAllocationMethodModal: () => void;
  getDevArea: () => Promise<void>;
  onAllocationManagerChange: (
    allocationManager: AllocationManagerResponse | null
  ) => void;
}) => {
  const [selectedWell, setSelectedWell] = useState<SelectedWell>(() =>
    defaultSelectedWell(developmentArea)
  );
  // State management
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [customRoyaltyRate, setCustomRoyaltyRate] = useState<string>(
    ROYALTY_RATE_OPTIONS.TWELVE_POINT_FIVE
  );
  const [isRoyaltyModalOpen, setIsRoyaltyModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<AssetType>(ASSET_TYPES.CRM);
  const [, setTitleHasWarning] = useState<boolean>(false);

  const [activeAllocationMethod, setActiveAllocationMethod] = useState<
    'acreage' | 'lateral_length'
  >('acreage');

  // Atoms and hooks
  const allocationManagerAtom = useAllocationManagerAtom(
    selectedWell.allocationManagerId,
    selectedVersion
  );
  const [allocationManagerResult, getAllocationManager] = useAtom(
    allocationManagerAtom
  );
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
  const versionsAtom = useAllocationManagerVersionsAtom(
    selectedWell.allocationManagerId,
    versionsRefreshKey
  );
  const allocationVersionsResult = useAtomValue(versionsAtom);
  const wellFeaturesAtom = useWellFeaturesAtom(selectedWell?.uwi ?? '');
  const wellFeaturesResult = useAtomValue(wellFeaturesAtom);

  // Slots Details lists a row per (well, agreement), so the selected row can belong
  // to another unit. The map follows the selection — a JOA row shows the JOA's
  // boundary with the wellbore on it, rather than leaving the outline on this page's
  // unit where the well appears to sit outside anything. Null while on our own row,
  // which skips the request.
  const selectedRowUnitName =
    selectedWell.slotUnit && selectedWell.slotUnit !== developmentArea.name
      ? selectedWell.slotUnit
      : null;
  const selectedRowUnitAtom = useUnitByNameAtom(selectedRowUnitName);
  const selectedRowUnitResult = useAtomValue(selectedRowUnitAtom);
  const selectedWellAtom = useWellAtom(selectedWell?.uwi);
  const well = useAtomValue(selectedWellAtom);
  const [currentOverlapThreshold, setOverlapThreshold] = useState<number>(3);
  const [participationFactorThreshold, setParticipationFactorThreshold] =
    useState<number | null>(DEFAULT_PARTICIPATION_FACTOR_THRESHOLD);

  // Derived state
  const allocationManager = !allocationManagerResult
    ? null
    : ('loading' in allocationManagerResult &&
          allocationManagerResult.loading) ||
        !('data' in allocationManagerResult) ||
        !allocationManagerResult.data ||
        'error' in allocationManagerResult.data
      ? null
      : allocationManagerResult.data;

  const versions = useMemo(() => {
    if (!allocationVersionsResult) return [];
    if (
      allocationVersionsResult.loading ||
      !allocationVersionsResult.data ||
      'error' in allocationVersionsResult.data
    ) {
      return [];
    }
    return allocationVersionsResult.data.versions;
  }, [allocationVersionsResult]);

  const latestAllocationManagerVersion = versions?.length
    ? versions[versions.length - 1]
    : null;

  const filteredVersions = useMemo(() => {
    if (!page.majorVersionNumber || !versions?.length) {
      return versions?.slice().reverse();
    }

    return versions
      .filter((version) => {
        const majorVersion = parseInt(version.split('.')[0]);

        const selectedMajorVersion = page.majorVersionNumber;
        return majorVersion === selectedMajorVersion;
      })
      .reverse();
  }, [page.majorVersionNumber, versions]);

  const wellFeatures = !wellFeaturesResult
    ? null
    : ('loading' in wellFeaturesResult && wellFeaturesResult.loading) ||
        !('data' in wellFeaturesResult) ||
        !wellFeaturesResult.data ||
        'error' in wellFeaturesResult.data
      ? null
      : wellFeaturesResult.data;

  useEffect(() => {
    if (filteredVersions?.length > 0) {
      return setSelectedVersion(filteredVersions[0]);
    }
  }, [filteredVersions]);

  // Pass allocationManager to parent
  useEffect(() => {
    onAllocationManagerChange(allocationManager);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationManager]);

  const allocationManagerLineItemsLoaded =
    !selectedWell.allocationManagerId ||
    (allocationManagerResult &&
      'loading' in allocationManagerResult &&
      !allocationManagerResult.loading);

  const wellData =
    !well || !well.data || 'error' in well.data ? undefined : well.data;

  // Falls back to this page's unit while the other unit loads, or if it fails —
  // the map and ownership panels always have a unit to read.
  const selectedRowUnit =
    !selectedRowUnitResult ||
    ('loading' in selectedRowUnitResult && selectedRowUnitResult.loading) ||
    !('data' in selectedRowUnitResult) ||
    !selectedRowUnitResult.data ||
    'error' in selectedRowUnitResult.data
      ? null
      : selectedRowUnitResult.data;
  // The unit the selected row belongs to. Everything keyed to the row — the map
  // boundary and the ownership panels — reads this rather than the page's unit, so a
  // JOA row reports the JOA's ownership instead of the D&S unit's numbers under a
  // JOA heading.
  const selectedUnit = selectedRowUnit ?? developmentArea;

  // Additional state
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [selectedTractId, setSelectedTractId] = useState<number | null>(null);
  const [selectedTractName, setSelectedTractName] = useState<string | null>(
    null
  );
  const [selectedAssetGeojson, setSelectedAssetGeojson] = useState<any | null>(
    null
  );
  const [selectedIntersectionGeojson, setSelectedIntersectionGeojson] =
    useState<any | null>(null);
  const [crmTractGeojsons, setCrmTractGeojsons] = useState<any[] | null>(null);
  const [crmSubtractGeojsons, setCrmSubtractGeojsons] = useState<any[] | null>(
    null
  );
  const [titleAssetGeojsons, setTitleAssetGeojsons] = useState<any[] | null>(
    null
  );

  // Memoized callbacks for NriTable
  const handleNriTractsCollected = useCallback((tractGeojsons: any[]) => {
    setCrmTractGeojsons(tractGeojsons);
  }, []);

  const handleNriSubtractsCollected = useCallback((subtractGeojsons: any[]) => {
    setCrmSubtractGeojsons(subtractGeojsons);
  }, []);
  const [showSuccessToast, setShowSuccessToast] = useState<{
    show: boolean;
    isRemove: boolean;
  }>({
    show: false,
    isRemove: false,
  });
  // State for royalty rate notifications
  const [royaltyToast, setRoyaltyToast] = useState<{
    show: boolean;
    message: string;
    subtitle: string;
  }>({
    show: false,
    message: '',
    subtitle: '',
  });

  const [successToast, setSuccessToast] = useState<{
    message: string;
    subtitle: string;
  } | null>(null);

  useEffect(() => {
    if (selectedTractId && allocationManager?.lineItems) {
      const selectedTract = allocationManager.lineItems.find(
        (item) => item.unitAllocationTract.id === selectedTractId
      );
      setSelectedTractName(
        selectedTract?.unitAllocationTract.tractName || null
      );
    } else {
      setSelectedTractName(null);
    }
  }, [selectedTractId, allocationManager?.lineItems]);

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version);
  };

  const handleWellSelect = (
    wellUWI: string | null,
    wellName: string | null,
    slotUnit: string | null
  ) => {
    if (!wellUWI) {
      setSelectedWell({
        uwi: null,
        allocationManagerId: null,
        wellName: wellName,
        slotUnit: slotUnit,
      });
      return;
    }
    // unitWellAssignments only holds THIS unit's agreements, so a lookup by
    // UWI alone answers the wrong question for a row belonging to another
    // unit: a well allocated both here and elsewhere would resolve to this
    // unit's allocation manager whichever of its two rows was clicked.
    const assignedHere = slotUnit === developmentArea.name;

    setSelectedWell({
      uwi: wellUWI,
      allocationManagerId: assignedHere
        ? (developmentArea.unitWellAssignments.find(
            (well) => well.wellUWI === wellUWI
          )?.allocationManagerId ?? 0)
        : null,
      wellName: wellName,
      slotUnit: slotUnit,
    });
  };

  // Whether the selected row is a well actually allocated under THIS unit, as opposed
  // to one of the intersecting-but-unassigned rows Slots Details also lists.
  const selectedWellIsAllocatedHere = Boolean(
    selectedWell.uwi &&
    selectedWell.slotUnit === developmentArea.name &&
    developmentArea.unitWellAssignments.some(
      (assignment) => assignment.wellUWI === selectedWell.uwi
    )
  );

  const [allocationMethodUpdateState, updateAllocationMethod] = useAtom(
    allocationMethodUpdateAtom
  );
  const [allocationMethodError, setAllocationMethodError] = useState<
    string | null
  >(null);
  const [allocationMethodWorkflow, setAllocationMethodWorkflow] = useState<{
    channel: string | null;
    status: 'idle' | 'processing' | 'processed' | 'failed';
  }>({
    channel: null,
    status: 'idle',
  });

  useEffect(() => {
    // Don't subscribe if no channel or workflow already complete
    if (
      !allocationMethodWorkflow.channel ||
      allocationMethodWorkflow.status === 'processed' ||
      allocationMethodWorkflow.status === 'failed'
    ) {
      return;
    }

    const subscription = subscribe(allocationMethodWorkflow.channel);

    // Listen for process status changes
    subscription.bind(
      'crm-workflow-process-status-changed',
      (eventData: { overall_status?: string; details?: string }) => {
        if (eventData?.overall_status === 'processed') {
          // Success - update state (triggers modal close and data reload)
          setAllocationMethodWorkflow((prev) => ({
            ...prev,
            status: 'processed',
          }));
        } else if (eventData?.overall_status === 'failed') {
          // Failed - update state and show error
          setAllocationMethodWorkflow((prev) => ({
            ...prev,
            status: 'failed',
          }));
          setAllocationMethodError(eventData.details || 'Workflow failed');
        }
      }
    );

    // Cleanup subscription automatically when effect re-runs or unmounts
    return () => subscription.unsubscribe();
  }, [allocationMethodWorkflow.channel, allocationMethodWorkflow.status]);

  // Handle successful workflow completion
  useEffect(() => {
    if (allocationMethodWorkflow.status === 'processed') {
      clearNriTableVerificationState();
      closeAllocationMethodModal();
      getDevArea();
      // Reset workflow state after handling
      setAllocationMethodWorkflow({ channel: null, status: 'idle' });
    }
  }, [allocationMethodWorkflow.status, closeAllocationMethodModal, getDevArea]);

  // Helper to check if response is unchanged (no workflow)
  const isUnchangedResponse = (
    data: AllocationMethodUpdateResponse | AllocationMethodUnchangedResponse
  ): data is AllocationMethodUnchangedResponse => {
    return 'unchanged' in data && data.unchanged === true;
  };

  const handleUpdateAllocation = (
    allocationMethod: 'acreage' | 'lateral_length'
  ) => {
    if (!selectedWell.allocationManagerId) return;

    const url = `/allocation_managers/${selectedWell.allocationManagerId}/allocation_method`;
    const payload = { allocation_method: allocationMethod };

    updateAllocationMethod({
      url,
      type: 'PATCH',
      data: payload,
      onSuccess: (
        data:
          | AllocationMethodUpdateResponse
          | AllocationMethodUnchangedResponse
          | null
      ) => {
        if (!data) return;

        // Handle unchanged response (no workflow created)
        if (isUnchangedResponse(data)) {
          closeAllocationMethodModal();
          setAllocationMethodError(null);
          return;
        }

        // Workflow response - set up state for Pusher subscription
        const { channel, shouldSubscribe } = data.resources.notifications;

        if (!shouldSubscribe) {
          clearNriTableVerificationState();
          // Process already complete (shouldn't happen normally)
          closeAllocationMethodModal();
          getDevArea();
          setAllocationMethodError(null);
          return;
        }

        // Set workflow state - this triggers the Pusher subscription useEffect
        setAllocationMethodWorkflow({
          channel,
          status: 'processing',
        });
        setAllocationMethodError(null);
      },
      onError: (error?: unknown) => {
        setAllocationMethodWorkflow({ channel: null, status: 'idle' });
        setAllocationMethodError(
          (error as { message?: string })?.message ||
            'Failed to update allocation method'
        );
      },
    });
  };

  useEffect(() => {
    if (allocationManager?.allocationMethod) {
      setActiveAllocationMethod(
        allocationManager.allocationMethod.toLowerCase() === 'acreage'
          ? 'acreage'
          : 'lateral_length'
      );
    }
  }, [allocationManager]);

  return (
    <>
      {successToast && (
        <SuccessToast
          message={successToast.message}
          subtitle={successToast.subtitle}
          onClose={() => {
            setSuccessToast(null);
            return true;
          }}
        />
      )}
      <Section className={style.unitDetailsSection}>
        <Grid className="bx--no-gutter" fullWidth>
          <Row>
            <Column lg={16}>
              {!allocationManagerLineItemsLoaded ? (
                <SkeletonText width="100%" heading />
              ) : (
                developmentArea && (
                  <UnitDetailsRibbon
                    name={developmentArea.name}
                    state={developmentArea.state ?? '--'}
                    currentOperator={wellData?.currentOperator ?? '--'}
                    allocationMethod={allocationManager?.allocationMethod ?? ''}
                    basin={developmentArea.basinName ?? ''}
                    tier={developmentArea.tierName ?? ''}
                    grossAcres={developmentArea.acreage ?? 0}
                    tractLength={developmentArea.length ?? 0}
                    tractWidth={developmentArea.width ?? 0}
                    isPlatBased={developmentArea.filedWellOnly}
                    unitType={developmentArea.unitType ?? ''}
                    ltdCapable={developmentArea.ltdCapable ?? false}
                  />
                )
              )}
            </Column>
          </Row>
        </Grid>
      </Section>

      <Section className={style.allocationManagerSection}>
        <Grid className="bx--no-gutter" fullWidth>
          <Tile className={style.tilePadding}>
            <Row>
              <Column md={12} lg={10}>
                <Section
                  header="Allocation Manager"
                  headerActions={
                    allocationManager && wellData && developmentArea ? (
                      <AllocationManagerHeaderActions
                        allocationManager={allocationManager}
                        latestAllocationManagerVersion={
                          latestAllocationManagerVersion
                        }
                        wellData={wellData}
                        versions={filteredVersions}
                        selectedVersion={selectedVersion}
                        onVersionChange={handleVersionChange}
                        onAssignDocument={async (isRemove: boolean) => {
                          await getAllocationManager();
                          setVersionsRefreshKey((prev) => prev + 1);
                          if (filteredVersions?.length > 0) {
                            setSelectedVersion(
                              filteredVersions[filteredVersions.length - 1]
                            );
                          }
                          setShowSuccessToast({
                            show: true,
                            isRemove,
                          });
                        }}
                        supportingFilesHref={
                          wellData.resources.supportingFiles.href
                        }
                        developmentAreaId={String(developmentArea.id)}
                        onOpenAllocationMethodModal={openAllocationMethodModal}
                        hasAllocationManager={!!allocationManager}
                        hasLateralLengthAllocation={
                          !(
                            allocationManager?.allocationMethod?.toLowerCase() ===
                              'acreage' &&
                            allocationManager?.lineItems?.every(
                              (tract) =>
                                !(
                                  tract.unitAllocationTractLateralValue ||
                                  tract.unitAllocationTractLateralFactor
                                )
                            )
                          )
                        }
                      />
                    ) : null
                  }
                >
                  {!allocationManagerLineItemsLoaded ? (
                    <LoadingAllocationManager />
                  ) : !allocationManager ? (
                    selectedWell.slotUnit &&
                    selectedWell.slotUnit !== developmentArea.name ? (
                      <InlineNotification
                        kind="info"
                        lowContrast
                        hideCloseButton
                        title={`${
                          selectedWell.uwi ||
                          selectedWell.wellName ||
                          'This slot'
                        } is not tied to the current unit.`}
                        subtitle={
                          (
                            <span>
                              <Link
                                to={`/development_areas/${selectedWell.slotUnit}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {selectedWell.slotUnit}
                              </Link>{' '}
                              will take you to its Unit page to see any
                              available Allocation Manager data.
                            </span>
                          ) as unknown as string
                        }
                      />
                    ) : (
                      <InlineNotification
                        kind="info"
                        title="No data is available at this time."
                        lowContrast
                        subtitle="Please contact a GIS team for assistance"
                        hideCloseButton
                      />
                    )
                  ) : (
                    <>
                      {showSuccessToast.show && (
                        <SuccessToast
                          message={
                            showSuccessToast.isRemove
                              ? 'Document Removed'
                              : 'Document Assigned'
                          }
                          subtitle={
                            showSuccessToast.isRemove
                              ? 'You have successfully removed the document.'
                              : 'You have successfully assigned the document.'
                          }
                        />
                      )}

                      {royaltyToast.show && (
                        <SuccessToast
                          message={royaltyToast.message}
                          subtitle={royaltyToast.subtitle}
                          onClose={() => {
                            setRoyaltyToast({
                              show: false,
                              message: '',
                              subtitle: '',
                            });
                            return true;
                          }}
                        />
                      )}
                      <AllocationManager
                        allocationManagerData={allocationManager}
                        onLineItemClick={setSelectedTractId}
                        selectedTractId={selectedTractId}
                        selectedVersion={selectedVersion}
                      />
                    </>
                  )}
                </Section>
                <Section header="Slots Details">
                  <Tag
                    type="high-contrast"
                    size="md"
                    className={style.assignedToUnitTag}
                  >
                    Assigned to Unit
                  </Tag>

                  <div className={style.infoText}>
                    Ownership results are tied exclusively to the assigned slot;
                    other rows are informational only.
                  </div>

                  <SlotsDetail
                    unitName={developmentArea.name}
                    selectedWellUWI={selectedWell.uwi}
                    selectedWellName={selectedWell.wellName}
                    selectedSlotUnit={selectedWell.slotUnit}
                    onWellSelect={handleWellSelect}
                    onCompare={
                      // Comparison is per-well AND anchored on this unit: the left
                      // column is the current unit's agreement. Slots Details also
                      // lists wells that merely intersect the unit, and those have
                      // no agreement here — comparing from one would render an
                      // all-dashes left column that reads as "no allocation
                      // manager" rather than "not allocated under this unit".
                      selectedWellIsAllocatedHere
                        ? () => setComparisonModalOpen(true)
                        : undefined
                    }
                  />

                  <ComparisonModal
                    isOpen={comparisonModalOpen}
                    onClose={() => setComparisonModalOpen(false)}
                    wellUWI={selectedWell.uwi}
                    currentUnitName={developmentArea.name}
                  />
                </Section>
              </Column>
              <Column lg={6}>
                <Tile className={style.mapTile}>
                  {allocationManagerLineItemsLoaded ? (
                    <AllocationTractMap
                      allocationTracts={allocationManager?.lineItems ?? []}
                      developmentArea={selectedUnit}
                      selectedWellUWI={selectedWell?.uwi ?? ''}
                      selectedTractId={selectedTractId}
                      wellFeatures={wellFeatures ?? null}
                      selectedAssetGeojson={selectedAssetGeojson}
                      selectedIntersectionGeojson={selectedIntersectionGeojson}
                      crmTractGeojsons={crmTractGeojsons}
                      crmSubtractGeojsons={crmSubtractGeojsons}
                      titleAssetGeojsons={titleAssetGeojsons}
                    />
                  ) : (
                    <LoadingAllocationTractMap />
                  )}
                </Tile>
              </Column>
            </Row>
          </Tile>
        </Grid>
      </Section>

      {/* Ownership Section */}
      <Section>
        <Grid className="bx--no-gutter" fullWidth>
          <Tile>
            <Section header="Ownership">
              {!allocationManagerLineItemsLoaded ? (
                <DataTableSkeleton
                  rowCount={10}
                  columnCount={8}
                  headers={[
                    { header: 'Asset' },
                    { header: 'Owner' },
                    { header: 'Interest Type' },
                    { header: 'Decimal' },
                    { header: 'NRI' },
                    { header: 'Royalty Rate' },
                    { header: 'Tract' },
                    { header: 'Status' },
                  ]}
                />
              ) : (
                <div
                  className={classNames(
                    style.tabs,
                    style.tabsContained,
                    style.noFocusOutline
                  )}
                >
                  <Tabs
                    selectedIndex={activeTab === ASSET_TYPES.CRM ? 0 : 1}
                    onChange={({ selectedIndex }: { selectedIndex: number }) =>
                      setActiveTab(
                        selectedIndex === 0
                          ? ASSET_TYPES.CRM
                          : ASSET_TYPES.TITLE
                      )
                    }
                  >
                    <TabList
                      className={`${style.tabsContained} no-focus-outline`}
                      contained
                    >
                      <Tab id="crm-tracts" key="crm-tracts-tab">
                        CRM Tracts
                      </Tab>
                      <Tab id="title-assets" key="title-assets-tab">
                        Title Assets
                      </Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <div className={style.ownershipTableContainer}>
                          {/* <CRMAssetsTable
                            allocationManagerId={
                              selectedWell.allocationManagerId
                            }
                            activeAllocationMethod={activeAllocationMethod}
                            initialColumnFilter={
                              selectedTractName
                                ? [
                                    {
                                      column: 'uat',
                                      value: selectedTractName,
                                    },
                                  ]
                                : []
                            }
                            developmentAreaId={developmentArea.id}
                            onAssetSelect={(
                              assetGeojson,
                              intersectionGeojson
                            ) => {
                              setSelectedAssetGeojson(assetGeojson);
                              setSelectedIntersectionGeojson(
                                intersectionGeojson
                              );
                            }}
                            onTractsCollected={(tractGeojsons) => {
                              setCrmTractGeojsons(tractGeojsons);
                            }}
                            onSubtractsCollected={(subtractGeojsons) => {
                              setCrmSubtractGeojsons(subtractGeojsons);
                            }}
                            onWarningStateChange={setCrmHasWarning}
                            customRoyaltyRate={customRoyaltyRate}
                            overlapThreshold={currentOverlapThreshold}
                            onThresholdChange={(threshold) =>
                              setOverlapThreshold(threshold)
                            }
                            onThresholdReset={() => setOverlapThreshold(3)}
                            participationFactorThreshold={
                              participationFactorThreshold
                            }
                            onParticipationFactorThresholdChange={(
                              threshold: number | null
                            ) => {
                              setParticipationFactorThreshold(threshold);
                              if (threshold) {
                                setSuccessToast({
                                  message:
                                    'Tract Participation Adjustment Successfully Applied',
                                  subtitle: `Participating Tract factors ≥ ${
                                    threshold * 100
                                  }% will be rounded to 100%. This will affect NRI calculations.`,
                                });
                              }
                            }}
                          /> */}
                          <NriTable
                            unitId={selectedUnit.id ?? 0}
                            uwi={selectedWell.uwi}
                            wellName={selectedWell.wellName}
                            uat={selectedTractName}
                            onRowSelected={(row: TractNRI | null) => {
                              unstable_batchedUpdates(() => {
                                if (row) {
                                  // Collect subtract geojsons as selectedAssetGeojson
                                  const subtractGeojsons = (row.subtracts ?? [])
                                    .filter((subtract) => subtract.geojson)
                                    .map((subtract) => subtract.geojson);
                                  setSelectedAssetGeojson(
                                    subtractGeojsons.length > 0
                                      ? subtractGeojsons
                                      : null
                                  );

                                  // Collect intersection geojsons from subtracts
                                  const intersectionGeojsons = (
                                    row.subtracts ?? []
                                  )
                                    .filter(
                                      (subtract) => subtract.intersectionGeojson
                                    )
                                    .map(
                                      (subtract) => subtract.intersectionGeojson
                                    );
                                  setSelectedIntersectionGeojson(
                                    intersectionGeojsons.length > 0
                                      ? intersectionGeojsons
                                      : null
                                  );
                                } else {
                                  setSelectedAssetGeojson(null);
                                  setSelectedIntersectionGeojson(null);
                                }
                              });
                            }}
                            onTractsCollected={handleNriTractsCollected}
                            onSubtractsCollected={handleNriSubtractsCollected}
                          />
                        </div>
                      </TabPanel>
                      <TabPanel>
                        <div className={style.ownershipTableContainer}>
                          <TitleAssetsTable
                            allocationManagerId={
                              selectedWell.allocationManagerId
                            }
                            activeAllocationMethod={activeAllocationMethod}
                            initialColumnFilter={
                              selectedTractName
                                ? [
                                    {
                                      column: 'uat',
                                      value: selectedTractName,
                                    },
                                  ]
                                : []
                            }
                            developmentAreaId={selectedUnit.id}
                            onAssetSelect={(
                              assetGeojson,
                              intersectionGeojson
                            ) => {
                              setSelectedAssetGeojson(assetGeojson);
                              setSelectedIntersectionGeojson(
                                intersectionGeojson
                              );
                            }}
                            onAssetsCollected={(assetGeojsons) => {
                              setTitleAssetGeojsons(assetGeojsons);
                            }}
                            onWarningStateChange={setTitleHasWarning}
                            overlapThreshold={currentOverlapThreshold}
                            onThresholdChange={(threshold) =>
                              setOverlapThreshold(threshold)
                            }
                            onThresholdReset={() => setOverlapThreshold(3)}
                            participationFactorThreshold={
                              participationFactorThreshold
                            }
                            onParticipationFactorThresholdChange={(
                              threshold: number | null
                            ) => {
                              setParticipationFactorThreshold(threshold);
                              if (threshold) {
                                setSuccessToast({
                                  message:
                                    'Tract Participation Adjustment Successfully Applied',
                                  subtitle: `Participating Tract factors ≥ ${
                                    threshold * 100
                                  }% will be rounded to 100%. This will affect NRI calculations.`,
                                });
                              }
                            }}
                          />
                        </div>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </div>
              )}
            </Section>
          </Tile>
        </Grid>
      </Section>

      <RelatedWellItemsSection selectedWell={selectedWellAtom} />

      <RelatedWellFilesSection selectedWell={selectedWellAtom} />

      <RoyaltyRateModal
        isOpen={isRoyaltyModalOpen}
        onClose={() => setIsRoyaltyModalOpen(false)}
        customRoyaltyRate={customRoyaltyRate}
        onApply={(rate) => {
          setCustomRoyaltyRate(rate);
          if (rate === ROYALTY_RATE_OPTIONS.NONE) {
            setRoyaltyToast({
              show: true,
              message: 'Custom Royalty Rate Successfully Removed',
              subtitle: 'Custom Royalty Rate has been set back to None',
            });
          } else {
            setRoyaltyToast({
              show: true,
              message: 'Custom Royalty Rate Successfully Applied',
              subtitle: `Custom Royalty of ${rate} has been applied to CRM Assets`,
            });
          }
        }}
      />
      <AllocationMethodUpdateModal
        isOpen={isAllocationMethodModalOpen}
        onClose={closeAllocationMethodModal}
        onUpdateAllocation={handleUpdateAllocation}
        developmentAreaName={developmentArea.name}
        allocationMethod={allocationManager?.allocationMethod ?? null}
        isLoading={allocationMethodUpdateState.loading}
        isProcessingWorkflow={allocationMethodWorkflow.status === 'processing'}
        error={allocationMethodError}
      />
    </>
  );
};

// Main Show Development Area Page component
const ShowDevelopmentAreaPage = ({
  developmentAreaId,
}: {
  developmentAreaId?: string;
}) => {
  const urlDevelopmentAreaId = useParams<{ id: string }>().id;
  const devAreaId = developmentAreaId
    ? developmentAreaId
    : urlDevelopmentAreaId;
  const devAreaHref = `/development_areas/${devAreaId}/versions/current`;
  const [page, setPage] = useState<Page>({
    href: devAreaHref,
    majorVersionNumber: null,
  });

  const devAreaAtom = useDevelopmentAreaAtom(page.href);
  const [developmentArea, getDevArea] = useAtom(devAreaAtom);
  const [isAllocationMethodModalOpen, setIsAllocationMethodModalOpen] =
    useState(false);

  const openAllocationMethodModal = () => setIsAllocationMethodModalOpen(true);
  const closeAllocationMethodModal = () =>
    setIsAllocationMethodModalOpen(false);

  const [, setHasAllocationManager] = useState(false);

  const handleAllocationManagerChange = (
    allocationManager: AllocationManagerResponse | null
  ) => {
    setHasAllocationManager(
      allocationManager !== null && !isEmpty(allocationManager)
    );
  };

  useEffect(() => {
    if (developmentAreaId) {
      setPage({
        href: `/development_areas/${developmentAreaId}/versions/current`,
        majorVersionNumber: null,
      });
    }
  }, [developmentAreaId]);

  if (developmentArea.data && 'error' in developmentArea.data)
    return (
      <InlineNotification
        kind="error"
        title={developmentArea.data.error}
        lowContrast
      />
    );

  return (
    <>
      <Header
        developmentArea={developmentArea.data}
        getDevArea={getDevArea}
        page={page}
        setPage={setPage}
      />
      {developmentArea.data && (
        <>
          <Helmet>
            <title>{pageTitle(developmentArea.data.name)}</title>
          </Helmet>
          <PageContent
            developmentArea={developmentArea.data}
            page={page}
            isAllocationMethodModalOpen={isAllocationMethodModalOpen}
            closeAllocationMethodModal={closeAllocationMethodModal}
            openAllocationMethodModal={openAllocationMethodModal}
            getDevArea={getDevArea}
            onAllocationManagerChange={handleAllocationManagerChange}
          />
          <EventsContent developmentArea={developmentArea.data} />
        </>
      )}
    </>
  );
};

export { ShowDevelopmentAreaPage };
